'use client'
import { AppRoutes } from '@/routes'
import { cn } from '@/utils/cn'
import Link from 'next/link'
import { useSelectedLayoutSegments } from 'next/navigation'
import { PropsWithChildren, ReactNode, useMemo } from 'react'

type IconProps = {
  isActive: boolean
}

type NavItem = {
  href: string
  Icon: (props: IconProps) => ReactNode
  title: string
  heading?: string
  segments: string[][]
}

export const BottomNavLayout = (props: PropsWithChildren) => {
  const segments = useSelectedLayoutSegments()
  const navItems: NavItem[] = useMemo(
    () => [
      {
        href: AppRoutes.home(),
        Icon: IconHome,
        title: '홈',
        segments: [
          ['(landing)'],
          ['tommorrows'],
          ['todays'],
          ['happy-manwon'],
          ['brands'],
          ['food-hall'],
          ['now-lowest'],
          ['week'],
          ['month'],
        ],
      },
      {
        // TODO change href to category page
        href: AppRoutes.search({}),
        Icon: IconSearch,
        title: '검색',
        heading: '검색',
        segments: [['search']],
      },
      {
        href: AppRoutes.myNotifications(),
        Icon: IconAlarm,
        title: '가격알림',
        heading: '가격알림',
        segments: [['my', 'notifications']],
      },
      {
        href: AppRoutes.myPage(),
        Icon: IconMy,
        title: 'MY',
        heading: '마이페이지',
        segments: [
          ['my', '(home)'],
          ['my', 'notifications'],
          ['my', 'settings'],
        ],
      },
    ],
    [],
  )
  const noActiveItemSegments = ['search']

  const activeItem = useMemo(
    () => findActiveNav(navItems, segments),
    [navItems, segments],
  )

  const showNav = activeItem || noActiveItemSegments.includes(segments[0])

  return (
    <div className={cn(showNav && 'pb-[64px]')}>
      <h1 className="sr-only">똑소</h1>
      {props.children}
      {showNav && (
        <div className="fixed bottom-0 left-0 w-full">
          <div className="screen m-auto flex justify-around border-t border-[#E4E4E4] bg-white">
            {navItems.map((item) => {
              const { href, Icon, title, heading } = item
              const isActive = activeItem == item
              return (
                <Link
                  key={title}
                  href={href}
                  className="relative mb-[8px] mt-[7px] flex size-[48px] flex-col items-center gap-[4px]"
                >
                  <Icon isActive={isActive} />
                  <span
                    className={cn(
                      'text-[10px]',
                      isActive ? 'text-primary' : 'text-[#CBCBCB]',
                    )}
                  >
                    {title}
                    {heading && <h2 className="sr-only">{heading}</h2>}
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

type SegmentsRecord = {
  segments: string[]
  item: NavItem
}

const descendingOrderOfSegments = (
  recordA: SegmentsRecord,
  recordB: SegmentsRecord,
): number => recordB.segments.length - recordA.segments.length

const getIsActiveNav =
  (currentSegments: string[]) => (record: SegmentsRecord) =>
    record.segments.every((seg, index) => currentSegments[index] === seg)

const findActiveNav = (
  items: NavItem[],
  currentSegments: string[],
): NavItem | undefined => {
  const segments = items.reduce<SegmentsRecord[]>((result, item) => {
    item.segments.forEach((segments) => {
      result.push({
        segments,
        item,
      })
    })
    return result
  }, [])

  const isActiveSegment = getIsActiveNav(currentSegments)
  return segments.sort(descendingOrderOfSegments).find(isActiveSegment)?.item
}

const IconSearch = ({ isActive }: IconProps) => {
  if (isActive) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.02614 6.02609C3.555 8.49723 3.555 12.5037 6.02614 14.9749C8.49728 17.446 12.5038 17.446 14.9749 14.9749C17.4461 12.5037 17.4461 8.49723 14.9749 6.02609C12.5038 3.55495 8.49728 3.55495 6.02614 6.02609ZM4.75027 16.2508C1.57448 13.075 1.57448 7.926 4.75027 4.75022C7.92606 1.57443 13.075 1.57443 16.2508 4.75022C19.4266 7.92601 19.4266 13.075 16.2508 16.2508C13.075 19.4265 7.92606 19.4265 4.75027 16.2508Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M20.1319 20.8024L15.3203 16.5923L16.5085 15.2344L21.3201 19.4445C21.6951 19.7726 21.7331 20.3426 21.405 20.7176C21.0769 21.0926 20.5069 21.1305 20.1319 20.8024Z"
          fill="#151515"
        />
      </svg>
    )
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.02614 6.02609C3.555 8.49723 3.555 12.5037 6.02614 14.9749C8.49728 17.446 12.5038 17.446 14.9749 14.9749C17.4461 12.5037 17.4461 8.49723 14.9749 6.02609C12.5038 3.55495 8.49728 3.55495 6.02614 6.02609ZM4.75027 16.2508C1.57448 13.075 1.57448 7.926 4.75027 4.75022C7.92606 1.57443 13.075 1.57443 16.2508 4.75022C19.4266 7.92601 19.4266 13.075 16.2508 16.2508C13.075 19.4265 7.92606 19.4265 4.75027 16.2508Z"
        fill="#878787"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.1319 20.8024L15.3203 16.5923L16.5085 15.2344L21.3201 19.4445C21.6951 19.7726 21.7331 20.3426 21.405 20.7176C21.0769 21.0926 20.5069 21.1305 20.1319 20.8024Z"
        fill="#878787"
      />
    </svg>
  )
}

const IconHome = ({ isActive }: IconProps) => {
  if (isActive) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M15.9932 7.34846L13.2797 5.07566C12.5366 4.4533 11.4543 4.4533 10.7113 5.07566L7.99773 7.34846L4.71579 10.0974C4.2621 10.4774 4 11.0388 4 11.6306V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V11.632C20 11.0394 19.7372 10.4773 19.2825 10.0973L15.9932 7.34846Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.2297 4.50069C11.2514 3.64495 12.7396 3.64495 13.7613 4.50069L16.4741 6.77296L19.7634 9.5218C20.3887 10.0443 20.75 10.8172 20.75 11.632V18C20.75 19.5188 19.5188 20.75 18 20.75H6C4.48122 20.75 3.25 19.5188 3.25 18V11.6306C3.25 10.8169 3.61039 10.0449 4.23421 9.52241L10.2297 4.50069ZM12.7981 5.65062C12.3337 5.26164 11.6572 5.26164 11.1928 5.65062L5.19737 10.6723C4.91381 10.9098 4.75 11.2607 4.75 11.6306V18C4.75 18.6904 5.30964 19.25 6 19.25H18C18.6904 19.25 19.25 18.6904 19.25 18V11.632C19.25 11.2616 19.0858 10.9103 18.8016 10.6728L12.7981 5.65062Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.25 16C7.25 15.5858 7.58579 15.25 8 15.25H16C16.4142 15.25 16.75 15.5858 16.75 16C16.75 16.4142 16.4142 16.75 16 16.75H8C7.58579 16.75 7.25 16.4142 7.25 16Z"
          fill="white"
        />
      </svg>
    )
  }
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.2297 4.50069C11.2514 3.64495 12.7396 3.64495 13.7613 4.50069L16.4741 6.77296L19.7634 9.5218C20.3887 10.0443 20.75 10.8172 20.75 11.632V18C20.75 19.5188 19.5188 20.75 18 20.75H6C4.48122 20.75 3.25 19.5188 3.25 18V11.6306C3.25 10.8169 3.61039 10.0449 4.23421 9.52241L10.2297 4.50069ZM12.7981 5.65062C12.3337 5.26164 11.6572 5.26164 11.1928 5.65062L5.19737 10.6723C4.91381 10.9098 4.75 11.2607 4.75 11.6306V18C4.75 18.6904 5.30964 19.25 6 19.25H18C18.6904 19.25 19.25 18.6904 19.25 18V11.632C19.25 11.2616 19.0858 10.9103 18.8016 10.6728L12.7981 5.65062Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.25 16C7.25 15.5858 7.58579 15.25 8 15.25H16C16.4142 15.25 16.75 15.5858 16.75 16C16.75 16.4142 16.4142 16.75 16 16.75H8C7.58579 16.75 7.25 16.4142 7.25 16Z"
        fill="#CBCBCB"
      />
    </svg>
  )
}

const IconMenu = ({ isActive }: IconProps) => {
  if (isActive) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="24"
        viewBox="0 0 25 24"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.75 6C4.75 5.58579 5.08579 5.25 5.5 5.25H19.5C19.9142 5.25 20.25 5.58579 20.25 6C20.25 6.41421 19.9142 6.75 19.5 6.75H5.5C5.08579 6.75 4.75 6.41421 4.75 6Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.75 12C4.75 11.5858 5.08579 11.25 5.5 11.25H19.5C19.9142 11.25 20.25 11.5858 20.25 12C20.25 12.4142 19.9142 12.75 19.5 12.75H5.5C5.08579 12.75 4.75 12.4142 4.75 12Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.75 18C4.75 17.5858 5.08579 17.25 5.5 17.25H19.5C19.9142 17.25 20.25 17.5858 20.25 18C20.25 18.4142 19.9142 18.75 19.5 18.75H5.5C5.08579 18.75 4.75 18.4142 4.75 18Z"
          fill="#151515"
        />
      </svg>
    )
  }
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.75 6C4.75 5.58579 5.08579 5.25 5.5 5.25H19.5C19.9142 5.25 20.25 5.58579 20.25 6C20.25 6.41421 19.9142 6.75 19.5 6.75H5.5C5.08579 6.75 4.75 6.41421 4.75 6Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.75 12C4.75 11.5858 5.08579 11.25 5.5 11.25H19.5C19.9142 11.25 20.25 11.5858 20.25 12C20.25 12.4142 19.9142 12.75 19.5 12.75H5.5C5.08579 12.75 4.75 12.4142 4.75 12Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.75 18C4.75 17.5858 5.08579 17.25 5.5 17.25H19.5C19.9142 17.25 20.25 17.5858 20.25 18C20.25 18.4142 19.9142 18.75 19.5 18.75H5.5C5.08579 18.75 4.75 18.4142 4.75 18Z"
        fill="#CBCBCB"
      />
    </svg>
  )
}

const IconAlarm = ({ isActive }: IconProps) => {
  if (isActive) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="24"
        viewBox="0 0 25 24"
        fill="none"
      >
        <path
          d="M15.5 18C15.5 19.6577 14.1577 21 12.5 21C10.8423 21 9.5 19.6577 9.5 18"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.25 18C10.25 19.2435 11.2565 20.25 12.5 20.25C13.7435 20.25 14.75 19.2435 14.75 18H16.25C16.25 20.0719 14.5719 21.75 12.5 21.75C10.4281 21.75 8.75 20.0719 8.75 18H10.25Z"
          fill="#151515"
        />
        <path
          d="M19.4982 18C20.0505 18 20.5079 17.5483 20.4033 17.006C20.2469 16.1948 19.9025 15.435 19.4101 14.7512C19.2769 14.5662 19.1967 14.3467 19.1967 14.1187V10.5667C19.1967 6.94831 16.1842 4 12.5073 4C8.83042 4 5.7997 6.95724 5.7997 10.5667V14.1187C5.7997 14.3467 5.71951 14.5662 5.58632 14.7512C5.09395 15.435 4.74953 16.1948 4.59312 17.006C4.48855 17.5483 4.94594 18 5.49823 18H19.4982Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.05168 10.5667C5.05168 6.52738 8.43401 3.25 12.5093 3.25C16.5866 3.25 19.9487 6.52041 19.9487 10.5667V14.1187C19.9487 14.1692 19.9669 14.2381 20.0208 14.313C20.5704 15.0763 20.9628 15.9358 21.1417 16.864C21.3545 17.9673 20.4179 18.75 19.5002 18.75H5.5002C4.58252 18.75 3.64591 17.9673 3.85866 16.864C4.03764 15.9358 4.43003 15.0763 4.97964 14.3129C5.0335 14.2381 5.05168 14.1692 5.05168 14.1187V10.5667ZM12.5093 4.75C9.23078 4.75 6.55168 7.38711 6.55168 10.5667V14.1187C6.55168 14.5242 6.40947 14.8942 6.19695 15.1894C5.76181 15.7938 5.46538 16.4538 5.33153 17.148C5.32896 17.1613 5.33055 17.1663 5.33099 17.1677L5.33104 17.1678C5.3319 17.1706 5.33531 17.1794 5.34713 17.192C5.37304 17.2195 5.42672 17.25 5.5002 17.25H19.5002C19.5737 17.25 19.6274 17.2195 19.6533 17.192C19.6651 17.1794 19.6685 17.1706 19.6694 17.1678C19.6697 17.1667 19.6716 17.1619 19.6689 17.148C19.535 16.4538 19.2386 15.7938 18.8035 15.1894C18.5909 14.8942 18.4487 14.5242 18.4487 14.1187V10.5667C18.4487 7.37621 15.7858 4.75 12.5093 4.75Z"
          fill="#151515"
        />
        <path
          d="M10.7952 15C10.411 15 10.0743 14.7431 9.97292 14.3726L8.74393 9.88297C8.62225 9.43845 8.95681 9 9.41768 9C9.74582 9 10.0297 9.22839 10.1 9.54891L10.8468 12.9545C10.8498 12.9682 10.8619 12.9779 10.8759 12.9779C10.8895 12.9779 10.9014 12.9686 10.9047 12.9554L11.742 9.59142C11.8285 9.24392 12.1406 9 12.4987 9C12.8564 9 13.1683 9.24338 13.2552 9.59038L14.1007 12.9674C14.1034 12.9784 14.1134 12.9862 14.1247 12.9862C14.1364 12.9862 14.1465 12.9781 14.149 12.9667L14.9039 9.54781C14.9745 9.22783 15.2581 9 15.5858 9C16.0469 9 16.3815 9.43898 16.2592 9.88359L15.0233 14.377C14.9221 14.745 14.5875 15 14.2058 15C13.8258 15 13.4922 14.7471 13.3895 14.3812L12.5322 11.3266C12.5284 11.3131 12.5162 11.3039 12.5022 11.3039C12.4883 11.3039 12.4761 11.3131 12.4723 11.3266L11.6159 14.3779C11.5127 14.7458 11.1773 15 10.7952 15Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.89844 10.9999C7.89844 10.6685 8.16707 10.3999 8.49844 10.3999H16.4984C16.8298 10.3999 17.0984 10.6685 17.0984 10.9999C17.0984 11.3313 16.8298 11.5999 16.4984 11.5999H8.49844C8.16707 11.5999 7.89844 11.3313 7.89844 10.9999Z"
          fill="white"
        />
      </svg>
    )
  }
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.25 18C10.25 19.2435 11.2565 20.25 12.5 20.25C13.7435 20.25 14.75 19.2435 14.75 18H16.25C16.25 20.0719 14.5719 21.75 12.5 21.75C10.4281 21.75 8.75 20.0719 8.75 18H10.25Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.05168 10.5667C5.05168 6.52738 8.43401 3.25 12.5093 3.25C16.5866 3.25 19.9487 6.52041 19.9487 10.5667V14.1187C19.9487 14.1692 19.9669 14.2381 20.0208 14.313C20.5704 15.0763 20.9628 15.9358 21.1417 16.864C21.3545 17.9673 20.4179 18.75 19.5002 18.75H5.5002C4.58252 18.75 3.64591 17.9673 3.85866 16.864C4.03764 15.9358 4.43003 15.0763 4.97964 14.3129C5.0335 14.2381 5.05168 14.1692 5.05168 14.1187V10.5667ZM12.5093 4.75C9.23078 4.75 6.55168 7.38711 6.55168 10.5667V14.1187C6.55168 14.5242 6.40947 14.8942 6.19695 15.1894C5.76181 15.7938 5.46538 16.4538 5.33153 17.148C5.32896 17.1613 5.33055 17.1663 5.33099 17.1677L5.33104 17.1678C5.3319 17.1706 5.33531 17.1794 5.34713 17.192C5.37304 17.2195 5.42672 17.25 5.5002 17.25H19.5002C19.5737 17.25 19.6274 17.2195 19.6533 17.192C19.6651 17.1794 19.6685 17.1706 19.6694 17.1678C19.6697 17.1667 19.6716 17.1619 19.6689 17.148C19.535 16.4538 19.2386 15.7938 18.8035 15.1894C18.5909 14.8942 18.4487 14.5242 18.4487 14.1187V10.5667C18.4487 7.37621 15.7858 4.75 12.5093 4.75Z"
        fill="#CBCBCB"
      />
      <path
        d="M10.7952 15C10.411 15 10.0743 14.7431 9.97292 14.3726L8.74393 9.88297C8.62225 9.43845 8.95681 9 9.41768 9C9.74582 9 10.0297 9.22839 10.1 9.54891L10.8468 12.9545C10.8498 12.9682 10.8619 12.9779 10.8759 12.9779C10.8895 12.9779 10.9014 12.9686 10.9047 12.9554L11.742 9.59142C11.8285 9.24392 12.1406 9 12.4987 9C12.8564 9 13.1683 9.24338 13.2552 9.59038L14.1007 12.9674C14.1034 12.9784 14.1134 12.9862 14.1247 12.9862C14.1364 12.9862 14.1465 12.9781 14.149 12.9667L14.9039 9.54781C14.9745 9.22783 15.2581 9 15.5858 9C16.0469 9 16.3815 9.43898 16.2592 9.88359L15.0233 14.377C14.9221 14.745 14.5875 15 14.2058 15C13.8258 15 13.4922 14.7471 13.3895 14.3812L12.5322 11.3266C12.5284 11.3131 12.5162 11.3039 12.5022 11.3039C12.4883 11.3039 12.4761 11.3131 12.4723 11.3266L11.6159 14.3779C11.5127 14.7458 11.1773 15 10.7952 15Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 11C8 10.7239 8.22386 10.5 8.5 10.5H16.5C16.7761 10.5 17 10.7239 17 11C17 11.2761 16.7761 11.5 16.5 11.5H8.5C8.22386 11.5 8 11.2761 8 11Z"
        fill="#CBCBCB"
      />
    </svg>
  )
}

const IconMy = ({ isActive }: IconProps) => {
  if (isActive) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12 11C13.6569 11 15 9.65685 15 8C15 6.34315 13.6569 5 12 5C10.3431 5 9 6.34315 9 8C9 9.65685 10.3431 11 12 11Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 5.75C10.7574 5.75 9.75 6.75736 9.75 8C9.75 9.24264 10.7574 10.25 12 10.25C13.2426 10.25 14.25 9.24264 14.25 8C14.25 6.75736 13.2426 5.75 12 5.75ZM8.25 8C8.25 5.92893 9.92893 4.25 12 4.25C14.0711 4.25 15.75 5.92893 15.75 8C15.75 10.0711 14.0711 11.75 12 11.75C9.92893 11.75 8.25 10.0711 8.25 8Z"
          fill="#151515"
        />
        <path
          d="M18.001 20C18.5533 20 19.0085 19.5497 18.9305 19.003C18.4469 15.6113 15.5322 13 12.001 13C8.46906 13 5.5539 15.6043 5.07117 19.0029C4.9935 19.5497 5.44871 20 6.001 20H18.001Z"
          fill="#151515"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.32952 18.8975C4.8641 15.1338 8.09177 12.25 12.0019 12.25C15.9121 12.25 19.1384 15.1415 19.6739 18.8971C19.8266 19.9677 18.9346 20.75 18.0019 20.75H6.00189C5.06905 20.75 4.17751 19.9677 4.32952 18.8975ZM12.0019 13.75C8.84814 13.75 6.24549 16.0749 5.81461 19.1084C5.81188 19.1276 5.8142 19.1397 5.8172 19.1486C5.82064 19.1587 5.82789 19.1726 5.84261 19.188C5.87357 19.2203 5.92939 19.25 6.00189 19.25H18.0019C18.0744 19.25 18.1302 19.2203 18.1611 19.188C18.1758 19.1727 18.183 19.1589 18.1864 19.1488C18.1894 19.14 18.1917 19.128 18.1889 19.1088C17.7572 16.0812 15.1541 13.75 12.0019 13.75Z"
          fill="#151515"
        />
      </svg>
    )
  }
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 5.75C10.7574 5.75 9.75 6.75736 9.75 8C9.75 9.24264 10.7574 10.25 12 10.25C13.2426 10.25 14.25 9.24264 14.25 8C14.25 6.75736 13.2426 5.75 12 5.75ZM8.25 8C8.25 5.92893 9.92893 4.25 12 4.25C14.0711 4.25 15.75 5.92893 15.75 8C15.75 10.0711 14.0711 11.75 12 11.75C9.92893 11.75 8.25 10.0711 8.25 8Z"
        fill="#CBCBCB"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.32952 18.8975C4.8641 15.1338 8.09177 12.25 12.0019 12.25C15.9121 12.25 19.1384 15.1415 19.6739 18.8971C19.8266 19.9677 18.9346 20.75 18.0019 20.75H6.00189C5.06905 20.75 4.17751 19.9677 4.32952 18.8975ZM12.0019 13.75C8.84814 13.75 6.24549 16.0749 5.81461 19.1084C5.81188 19.1276 5.8142 19.1397 5.8172 19.1486C5.82064 19.1587 5.82789 19.1726 5.84261 19.188C5.87357 19.2203 5.92939 19.25 6.00189 19.25H18.0019C18.0744 19.25 18.1302 19.2203 18.1611 19.188C18.1758 19.1727 18.183 19.1589 18.1864 19.1488C18.1894 19.14 18.1917 19.128 18.1889 19.1088C17.7572 16.0812 15.1541 13.75 12.0019 13.75Z"
        fill="#CBCBCB"
      />
    </svg>
  )
}
